// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { PersonCard, OrgChart, TeamList } from '@/components/advanced/people';

export default function PeoplePage() {
  // --- PersonCard mock data ---
  const samplePerson = {
    id: 'p1',
    firstName: 'Olivia',
    lastName: 'Bennett',
    email: 'olivia.bennett@example.com',
    phone: '+1 (555) 014-2233',
    role: 'Senior Product Designer',
    department: 'Design',
    organization: 'Acme Corp',
    location: 'San Francisco, CA',
    status: 'active',
    tags: ['Design', 'Lead'],
  };

  // --- OrgChart mock data ---
  const orgData = {
    id: 'ceo',
    name: 'Marcus Hale',
    title: 'Chief Executive Officer',
    department: 'Executive',
    children: [
      {
        id: 'cto',
        name: 'Priya Nair',
        title: 'Chief Technology Officer',
        department: 'Engineering',
        children: [
          { id: 'eng-1', name: 'Daniel Kim', title: 'Engineering Manager', department: 'Platform' },
          { id: 'eng-2', name: 'Sara Lopez', title: 'Engineering Manager', department: 'Mobile' },
        ],
      },
      {
        id: 'cmo',
        name: 'Tom Becker',
        title: 'Chief Marketing Officer',
        department: 'Marketing',
        children: [
          { id: 'mkt-1', name: 'Aisha Khan', title: 'Brand Lead', department: 'Brand' },
        ],
      },
    ],
  };

  // --- TeamList mock data ---
  const mockTeams = [
    {
      id: 't1',
      name: 'Platform Engineering',
      description: 'Builds and maintains the core services and infrastructure.',
      department: 'Engineering',
      tags: ['Backend', 'Infra'],
      lead: { id: 'm1', firstName: 'Daniel', lastName: 'Kim', role: 'Engineering Manager' },
      members: [
        { id: 'm1', firstName: 'Daniel', lastName: 'Kim' },
        { id: 'm2', firstName: 'Elena', lastName: 'Ortiz' },
        { id: 'm3', firstName: 'Jamal', lastName: 'Wright' },
        { id: 'm4', firstName: 'Yuki', lastName: 'Tanaka' },
        { id: 'm5', firstName: 'Noah', lastName: 'Patel' },
        { id: 'm6', firstName: 'Grace', lastName: 'Lin' },
      ],
      stats: { projects: 5, tasks: 38, completion: 72 },
    },
    {
      id: 't2',
      name: 'Brand & Design',
      description: 'Owns the visual identity and design system.',
      department: 'Marketing',
      tags: ['Design', 'Brand'],
      lead: { id: 'm7', firstName: 'Aisha', lastName: 'Khan', role: 'Brand Lead' },
      members: [
        { id: 'm7', firstName: 'Aisha', lastName: 'Khan' },
        { id: 'm8', firstName: 'Olivia', lastName: 'Bennett' },
        { id: 'm9', firstName: 'Liam', lastName: 'Carter' },
      ],
      stats: { projects: 3, tasks: 21, completion: 45 },
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>People Components</Title>
        <Text c="dimmed" size="sm">
          Components for displaying people, teams, and organizational hierarchy
        </Text>
      </Stack>

      {/* PersonCard */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PersonCard</Title>
            <Text size="sm" c="dimmed">
              Display an individual person with contact details, status, and actions. Supports
              four variants: default, compact, horizontal, and detailed.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (variants):</Text>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Stack gap="xs">
                <Text size="xs" c="dimmed">default</Text>
                <PersonCard
                  person={samplePerson}
                  variant="default"
                  onMessage={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              </Stack>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">compact</Text>
                <PersonCard
                  person={samplePerson}
                  variant="compact"
                  onMessage={() => {}}
                  onSchedule={() => {}}
                />
              </Stack>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">horizontal</Text>
                <PersonCard
                  person={samplePerson}
                  variant="horizontal"
                  onMessage={() => {}}
                  onEdit={() => {}}
                />
              </Stack>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">detailed</Text>
                <PersonCard
                  person={samplePerson}
                  variant="detailed"
                  onMessage={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              </Stack>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const person = {
  id: 'p1',
  firstName: 'Olivia',
  lastName: 'Bennett',
  email: 'olivia.bennett@example.com',
  phone: '+1 (555) 014-2233',
  role: 'Senior Product Designer',
  department: 'Design',
  organization: 'Acme Corp',
  location: 'San Francisco, CA',
  status: 'active',
  tags: ['Design', 'Lead'],
};

<PersonCard
  person={person}
  variant="detailed"
  onMessage={() => {}}
  onEdit={() => {}}
  onDelete={() => {}}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • person - PersonData object (required)
              <br />
              • variant - 'default' | 'compact' | 'horizontal' | 'detailed'
              <br />
              • showContact / showStatus / showActions - toggle sections
              <br />
              • onView / onEdit / onDelete / onMessage / onSchedule / onClick - handlers
              <br />
              • customActions - extra menu items
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* OrgChart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>OrgChart</Title>
            <Text size="sm" c="dimmed">
              Render an organizational hierarchy from a nested node tree. Supports tree,
              horizontal, and compact variants with collapsible nodes.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (tree):</Text>
            <OrgChart data={orgData} variant="tree" onNodeClick={() => {}} />
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (compact):</Text>
            <OrgChart data={orgData} variant="compact" onNodeClick={() => {}} />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const orgData = {
  id: 'ceo',
  name: 'Marcus Hale',
  title: 'Chief Executive Officer',
  department: 'Executive',
  children: [
    {
      id: 'cto',
      name: 'Priya Nair',
      title: 'Chief Technology Officer',
      department: 'Engineering',
      children: [
        { id: 'eng-1', name: 'Daniel Kim', title: 'Engineering Manager' },
        { id: 'eng-2', name: 'Sara Lopez', title: 'Engineering Manager' },
      ],
    },
  ],
};

<OrgChart
  data={orgData}
  variant="tree"
  onNodeClick={(node) => console.log(node)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • data - root OrgNode with nested children (required)
              <br />
              • variant - 'tree' | 'horizontal' | 'compact'
              <br />
              • onNodeClick - node click handler
              <br />
              • expandedByDefault / maxDepth / showCount - display options
              <br />
              • renderNode - custom node renderer
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* TeamList */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>TeamList</Title>
            <Text size="sm" c="dimmed">
              Display a collection of teams with leads, members, stats, and tags. Supports
              cards, list, and compact variants.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (cards):</Text>
            <TeamList
              teams={mockTeams}
              variant="cards"
              columns={2}
              onTeamClick={() => {}}
              onMemberClick={() => {}}
            />
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (list):</Text>
            <TeamList teams={mockTeams} variant="list" onTeamClick={() => {}} />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const teams = [
  {
    id: 't1',
    name: 'Platform Engineering',
    description: 'Builds and maintains the core services.',
    department: 'Engineering',
    tags: ['Backend', 'Infra'],
    lead: { id: 'm1', firstName: 'Daniel', lastName: 'Kim' },
    members: [
      { id: 'm1', firstName: 'Daniel', lastName: 'Kim' },
      { id: 'm2', firstName: 'Elena', lastName: 'Ortiz' },
      // ... more members
    ],
    stats: { projects: 5, tasks: 38, completion: 72 },
  },
];

<TeamList
  teams={teams}
  variant="cards"
  columns={2}
  onTeamClick={(team) => console.log(team)}
  onMemberClick={(member, team) => console.log(member, team)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • teams - array of TeamData (required)
              <br />
              • variant - 'cards' | 'list' | 'compact'
              <br />
              • onTeamClick / onMemberClick - handlers
              <br />
              • showMembers / showStats / maxMembersShown - display options
              <br />
              • columns - 1 | 2 | 3 | 4 (cards layout)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Directory & Profiles</Text>
              <Text size="xs" c="dimmed">
                Use PersonCard in member directories, contact lists, or profile previews with
                inline message and schedule actions.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Reporting Structure</Text>
              <Text size="xs" c="dimmed">
                Use OrgChart to visualize reporting lines, department hierarchy, and team
                structure with expandable nodes.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Team Overview</Text>
              <Text size="xs" c="dimmed">
                Use TeamList on dashboards to surface teams, their leads, member counts, and
                project completion at a glance.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
